import type {TableRowSelection} from 'antd/lib/table/interface';
import lodash from 'lodash-es';
import {
    type Dispatch,
    type Key,
    type SetStateAction,
    useMemo,
    useState,
} from 'react';

interface Props<T> {
    props?: TableRowSelection<T>;
    rowKey?: string;
}

export default function useSelection<T = any>(
    options: Props<T> = {},
): TableRowSelection<T> & {
    selected: Key[];
    selectedRows: T[];
    isEmpty: boolean;
    setSelected: Dispatch<SetStateAction<Key[]>>;
    clear(): void;
    length(): number;
    removeItem(key: Key): void;
    appendItem(key: Key, row?: T): void;
    appendItems(items: (Key | [Key, T])[]): void;
} {
    const {props, rowKey = 'id'} = options;

    const [selected, setSelected] = useState<Key[]>(
        props?.selectedRowKeys || [],
    );
    const [rowMap, setRowMap] = useState<Map<Key, T>>(new Map());

    const isEmpty = useMemo(() => lodash.isEmpty(selected), [selected]);

    return {
        ...props,
        selectedRowKeys: selected,
        preserveSelectedRowKeys: true,
        onChange: (keys: Key[], rows: T[], info) => {
            setSelected(keys);

            setRowMap(prev => {
                const newMap = new Map(prev);
                rows.forEach((row: any) => {
                    const key = (row as any)?.[rowKey];
                    newMap.set(key, row);
                });
                return newMap;
            });

            props?.onChange?.(keys, rows, info);
        },
        selected,
        selectedRows: selected.map(key => rowMap.get(key)!).filter(Boolean),
        setSelected,
        isEmpty,
        clear() {
            setSelected([]);
            setRowMap(new Map());
        },
        length() {
            return selected.length || 0;
        },
        removeItem(key: Key) {
            setSelected(prev => prev.filter(k => k !== key));
            setRowMap(prev => {
                const newMap = new Map(prev);
                newMap.delete(key);
                return newMap;
            });
        },
        appendItem(key: Key, row?: T) {
            setSelected(prev => (prev.includes(key) ? prev : [...prev, key]));
            if (row) {
                setRowMap(prev => {
                    const newMap = new Map(prev);
                    newMap.set(key, row);
                    return newMap;
                });
            }
        },
        appendItems(items: (Key | [Key, T])[]) {
            setSelected(prev => {
                const set = new Set(prev);
                items.forEach(item => {
                    const key = Array.isArray(item) ? item[0] : item;
                    set.add(key);
                });
                return Array.from(set);
            });
            setRowMap(prev => {
                const newMap = new Map(prev);
                items.forEach(item => {
                    if (Array.isArray(item)) {
                        newMap.set(item[0], item[1]);
                    }
                });
                return newMap;
            });
        },
    };
}
